import { useFormik } from 'formik'
import { FC, useMemo } from 'react'
import {
  ButtonType,
  showAlert,
  showConfirmAlert,
  showError,
} from '../../../../../../../_metronic/helpers'
import {
  Button,
  FormInput,
  FormTextArea,
  LoadingIndicator,
  useModal,
} from '../../../../../../components'
import {
  atualizaValorFatura,
  autorizarFatura,
  cancelarFatura,
  pagarFatura,
} from '../../core/_requests'
import { AtualizaFaturaDto, AtualizaValorAdicionalDto } from '../../core/_models'
import { faturaSchema } from './validation/faturaSchema'
import { useT } from '_metronic/i18n/Metronici18n'

interface IProps {
  action: 'autorizar' | 'cancelar' | 'pagar' | 'adicional'
  idFatura: number
}

export const FaturaModal: FC<IProps> = ({ action, idFatura }) => {
  const { closeModal } = useModal()
  const { t } = useT()

  const formik = useFormik({
    initialValues: {
      observacao: '',
      valor: undefined,
      action,
    },
    validationSchema: useMemo(() => faturaSchema(t), [t]),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      try {
        const isConfirmed = await showConfirmAlert({ confirmButtonText: t('common.yes') })
        if (!isConfirmed) return

        if (action === 'adicional') {
          const data: AtualizaValorAdicionalDto = {
            observacao: values.observacao.trim() || undefined,
            valor: Number(values.valor),
          }
          await atualizaValorFatura(idFatura, data)
          showAlert({
            title: 'Sucesso',
            text: 'Valor da fatura atualizado com sucesso!',
            icon: 'success',
          })
        } else {
          const data: AtualizaFaturaDto = {
            observacao: values.observacao.trim() || undefined,
          }
          if (action === 'autorizar') await autorizarFatura(idFatura, data)
          if (action === 'cancelar') await cancelarFatura(idFatura, data)
          if (action === 'pagar') await pagarFatura(idFatura, data)

          showAlert({
            title: 'Sucesso',
            text:
              action === 'pagar'
                ? t('alert.paga', { name: t('fatura.label.lowercase'), gender: t('fatura.gender') })
                : action === 'autorizar'
                ? t('alert.autorizada', { name: t('fatura.label'), gender: t('fatura.gender') })
                : t('alert.cancelada', { name: t('fatura.label'), gender: t('fatura.gender') }),
            icon: 'success',
          })
        }
        closeModal()
      } catch (error) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <FormTextArea
        name='observacao'
        formik={formik}
        entity='observacao'
        isDisabled={formik.isSubmitting}
        className='mb-3'
      />
      {action === 'adicional' && (
        <FormInput
          formik={formik}
          name='valor'
          type='number'
          entity='valor_adicional'
          infoText={t('valor_adicional.infotext')}
          isRequired
          highlight
        />
      )}

      <div className='d-flex justify-content-center mt-10'>
        <Button className='btn-secondary me-3' disabled={formik.isSubmitting} onClick={closeModal}>
          {t('common.cancel')}
        </Button>
        <Button type={ButtonType.Submit} className='btn-primary' disabled={formik.isSubmitting}>
          {formik.isSubmitting ? <LoadingIndicator /> : t('common.save')}
        </Button>
      </div>
    </form>
  )
}
